/**
 * ExpressionBuilderSection — the design-time syntax/output-format fields (and, where it applies, the
 * "Enable Expression Builder" toggle) for **every** control in the product that has an Expression
 * Builder behind it.
 *
 * Four editors use it:
 *
 *   - `TextEditor`, `TextareaEditor` (Plan/05 §3B) and `RichTextEditorEditor` (§3C) — the opt-in
 *     trigger beside a native element. These get the whole section, toggle included.
 *   - `ExpressionEditor` (Plan/05 §4, "Option B") — the standalone `expression` control. This one
 *     passes `showEnabledToggle={false}` and `configKey={null}`; see those props below.
 *
 * It is deliberately **not** wired into `JSONEditorEditor`, which reuses `TextareaEditor`'s rows
 * section but is a different control type (`json-editor`) with no case-block change in
 * `FormField.tsx` — offering the toggle there would produce a switch that silently does nothing.
 *
 * ── The two config layouts, and why one component serves both ──────────────────────────────────
 *
 * §3B/§3C nest everything under one `control.config.expressionBuilder` object rather than using
 * three flat keys. `config` is a shared bag per control, and `syntax`/`outputFormat` at the top
 * level would collide with the *same two key names* Option B's `expression` control uses. Nesting
 * also makes `enabled` unambiguous: the runtime gate is `config.expressionBuilder?.enabled === true`,
 * so an absent object is off, which is what every form that exists today has.
 *
 * Option B stores those two keys **flat** on `control.config`, because it has no third key to
 * disambiguate and no possibility of collision — a control of type `expression` has no other
 * Expression Builder to conflict with. That difference is a `configKey` prop rather than a second
 * copy of the component: before this, `ExpressionEditor.tsx` reimplemented the same two option
 * lists and the same select/help markup independently, in the same package, with nothing keeping
 * the two in step.
 *
 * The two literal unions are restated rather than imported from `@bizfirst/expressions-types`,
 * deliberately: this designer package must not acquire a dependency on the Expressions Studio chain
 * (and through it Tiptap) for two string unions used to build a `<select>`.
 */

import React from 'react';
import type { FormControl } from '@atlas-forms/types-js';
import {
  EXPRESSION_DEFAULT_SYNTAX,
  TEXT_EXPRESSION_DEFAULT_FORMAT,
} from '@atlas-forms/player-components-react';

/** Mirrors `WildcardSyntax` in `@bizfirst/expressions-types`. */
type ExpressionSyntax = 'AtBrace' | 'DoubleBrace' | 'AtSign' | 'PercentBrace';

/** Mirrors `EditorOutputFormat` in `@bizfirst/expressions-types`. */
type ExpressionOutputFormat = 'html' | 'text';

/** Mirrors `ExpressionBuilderFieldConfig` in `@atlas-forms/player-components-react`. */
interface ExpressionBuilderFieldConfig {
  enabled?: boolean;
  syntax?: ExpressionSyntax;
  outputFormat?: ExpressionOutputFormat;
}

/**
 * Labels show the delimiters themselves — "PercentBrace" means nothing to a schema author.
 *
 * No "(default)" is baked into any label here. It used to be, on `PercentBrace`, and that was a
 * third independent statement of the same fact: the runtime fallback, `DEFAULT_SYNTAX` below and
 * the label string could each be edited without the other two. It is now appended at render time
 * from `DEFAULT_SYNTAX`, exactly as the output-format list's marker already was.
 */
const SYNTAX_OPTIONS: ReadonlyArray<{ value: ExpressionSyntax; label: string }> = [
  { value: 'PercentBrace', label: '%{ … } — Percent Brace' },
  { value: 'AtBrace', label: '@{ … } — At Brace' },
  { value: 'DoubleBrace', label: '{{ … }} — Double Brace' },
  { value: 'AtSign', label: '@ … @ — At Sign' },
];

/**
 * "(default)" is appended at render time from `defaultOutputFormat`, so the marker follows the call
 * site instead of being baked into a literal that would lie on whichever type does not use it.
 */
const OUTPUT_FORMAT_OPTIONS: ReadonlyArray<{ value: ExpressionOutputFormat; label: string }> = [
  { value: 'text', label: 'Text — flatten to plain text' },
  { value: 'html', label: 'HTML — keep formatting marks' },
];

/**
 * The delimiter fallback, imported rather than restated so this panel's "(default)" marker and the
 * value `ExpressionBuilderTrigger`/`ExpressionControl` actually apply are the *same* literal.
 *
 * Unlike `defaultOutputFormat` this is not a prop: every editor that renders this section wants the
 * same delimiter default, and there is no per-control-type reason for one to differ.
 *
 * Read lazily (inside a function) rather than as a module-level `const`, and NOT cached in one:
 * `@atlas-forms/player-components-react` and this package have a real circular import (see that
 * package's `src/index.ts` comment on this exact constant), and in a build that consumes this
 * package's compiled `dist/` rather than aliasing it to source (e.g. app-studio-designer),
 * evaluating this at module load time can re-enter the still-initializing exporting module and
 * throw "Cannot access 'EXPRESSION_DEFAULT_SYNTAX' before initialization", crashing the app on
 * load. Reading it inside a function defers access to render time, after the whole module graph
 * has finished its synchronous top-level evaluation, which sidesteps the ordering hazard entirely.
 * The return type annotation still gives the same compile-time check the header describes.
 */
function getDefaultSyntax(): ExpressionSyntax {
  return EXPRESSION_DEFAULT_SYNTAX;
}

const DEFAULT_OUTPUT_FORMAT_HELP =
  'Keep this on Text unless the stored value is genuinely HTML — this field is a plain text box, ' +
  'so markup is shown as literal tags rather than rendered.';

const DEFAULT_HOST_CONFIG_HELP = (
  <p className="af-properties-tab__help">
    The wildcard catalog the builder browses is supplied at runtime by the hosting application
    (<code>configureExpressionBuilder()</code>), not by the form schema — a live API client is not
    serializable. Until a host configures it, the button renders disabled and the field stays fully
    editable by typing.
  </p>
);

export interface ExpressionBuilderSectionProps {
  control: FormControl;
  onConfigChange: (key: string, value: unknown) => void;
  /**
   * What the runtime falls back to when the field sets no `outputFormat` of its own.
   *
   * Never hardcode a literal at a call site: import the matching constant —
   * `TEXT_EXPRESSION_DEFAULT_FORMAT`/`RICH_TEXT_EXPRESSION_DEFAULT_FORMAT` from
   * `@atlas-forms/player-components-react` for §3B/§3C's opt-in trigger,
   * `EXPRESSION_CONTROL_DEFAULT_FORMAT` from `@atlas-forms/controls-advanced-react` for Option B's
   * control — so the "(default)" marker shown here and the fallback the runtime actually applies are
   * the *same value*, not two literals kept in step by a comment.
   */
  defaultOutputFormat?: ExpressionOutputFormat;
  /** Replaces the output-format explanation when the default one would be misleading. */
  outputFormatHelp?: string;
  /**
   * Heading for the section. Option B's editor calls itself "Expression Builder Configuration",
   * since for that control type the whole panel *is* the Expression Builder's configuration rather
   * than one opt-in section among others.
   */
  title?: string;
  /**
   * Where in `control.config` the two settings live.
   *
   *   - `'expressionBuilder'` (the default) — nested under that key, alongside `enabled`. Plan/05
   *     §3B/§3C's layout, needed because `config` is a shared bag and flat `syntax`/`outputFormat`
   *     keys would collide with Option B's.
   *   - `null` — flat on `control.config`. Option B's `expression` control, which owns its whole
   *     `config` and has nothing to collide with.
   */
  configKey?: string | null;
  /**
   * `false` omits the "Enable Expression Builder" checkbox and renders the syntax/format fields
   * unconditionally.
   *
   * Option B passes this: for a control of type `expression`, the control's *existence* is the
   * opt-in. A toggle there would be a switch that either does nothing or, worse, implies the field
   * can be turned into something else.
   */
  showEnabledToggle?: boolean;
  /** Replaces the closing "the catalog comes from the host at runtime" paragraph. */
  hostConfigHelp?: React.ReactNode;
}

/**
 * Reads the two (or three) settings from wherever `configKey` says they live, defensively: `config`
 * is `Record<string, unknown>` on a schema that may have been hand-authored or written by an older
 * designer build, so the nested object is not guaranteed to be an object at all.
 */
function readConfig(control: FormControl, configKey: string | null): ExpressionBuilderFieldConfig {
  if (configKey === null) return (control.config ?? {}) as ExpressionBuilderFieldConfig;
  const raw = control.config?.[configKey];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  return raw as ExpressionBuilderFieldConfig;
}

export function ExpressionBuilderSection({
  control,
  onConfigChange,
  defaultOutputFormat = TEXT_EXPRESSION_DEFAULT_FORMAT,
  outputFormatHelp = DEFAULT_OUTPUT_FORMAT_HELP,
  title = 'Expression Builder',
  configKey = 'expressionBuilder',
  showEnabledToggle = true,
  hostConfigHelp = DEFAULT_HOST_CONFIG_HELP,
}: ExpressionBuilderSectionProps): React.ReactElement {
  const config = readConfig(control, configKey);
  /** With no toggle, the settings are always shown — the control type itself is the opt-in. */
  const enabled = showEnabledToggle ? config.enabled === true : true;
  const syntax = config.syntax ?? getDefaultSyntax();
  const outputFormat = config.outputFormat ?? defaultOutputFormat;

  const enabledCheckboxID = `${control.id}-expression-builder-enabled`;
  const syntaxSelectID = `${control.id}-expression-builder-syntax`;
  const outputFormatSelectID = `${control.id}-expression-builder-output-format`;

  /**
   * Nested: writes the whole object back, because `onConfigChange` sets one `config` key at a time.
   * Flat: writes each key on its own, which is what Option B's control reads.
   */
  const update = (patch: Partial<ExpressionBuilderFieldConfig>): void => {
    if (configKey === null) {
      for (const [key, patchValue] of Object.entries(patch)) {
        onConfigChange(key, patchValue);
      }
      return;
    }
    onConfigChange(configKey, { ...config, ...patch });
  };

  return (
    <div className="af-properties-tab__section">
      <h4 className="af-properties-tab__subsection-title">{title}</h4>

      {showEnabledToggle && (
        <div className="af-properties-tab__checkbox-group">
          <label htmlFor={enabledCheckboxID} className="af-properties-tab__checkbox-label">
            <input
              id={enabledCheckboxID}
              type="checkbox"
              checked={config.enabled === true}
              onChange={(e) => update({ enabled: e.target.checked })}
            />
            {' '}Enable Expression Builder
          </label>
          <p className="af-properties-tab__help">
            Adds an "Edit using Expression Builder" button beside this field for authoring wildcard
            templates. Off by default. The field itself keeps working exactly as it does now — the
            button is an extra way to write the same value, never a replacement for typing in it.
          </p>
        </div>
      )}

      {enabled && (
        <>
          <div className="af-properties-tab__group">
            <label htmlFor={syntaxSelectID} className="af-properties-tab__label">
              Delimiter Syntax
            </label>
            <select
              id={syntaxSelectID}
              value={syntax}
              onChange={(e) => update({ syntax: e.target.value as ExpressionSyntax })}
              className="af-properties-tab__select"
              aria-label="Delimiter syntax"
            >
              {SYNTAX_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.value === getDefaultSyntax() ? `${option.label} (default)` : option.label}
                </option>
              ))}
            </select>
            <p className="af-properties-tab__help">
              The delimiter style the builder opens in. Authors can switch style inside the builder;
              whatever they confirm with is what gets written into the field.
            </p>
          </div>

          <div className="af-properties-tab__group">
            <label htmlFor={outputFormatSelectID} className="af-properties-tab__label">
              Output Format
            </label>
            <select
              id={outputFormatSelectID}
              value={outputFormat}
              onChange={(e) =>
                update({ outputFormat: e.target.value as ExpressionOutputFormat })
              }
              className="af-properties-tab__select"
              aria-label="Output format"
            >
              {OUTPUT_FORMAT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.value === defaultOutputFormat ? `${option.label} (default)` : option.label}
                </option>
              ))}
            </select>
            <p className="af-properties-tab__help">{outputFormatHelp}</p>
          </div>

          {hostConfigHelp}
        </>
      )}
    </div>
  );
}

export default ExpressionBuilderSection;
